/**
 * The interview-to-plan seam (spec #47 §2, §10 — the Opus task).
 *
 * WHERE THE SEAM IS, EXACTLY. #45 owns everything from the terminal
 * `POST /api/onboarding/transition` onward: brief v1, the plan scaffold
 * and drafter, `operating_plan_versions(status='draft')`, the tier-2
 * `ADOPT_PLAN_VERSION` proposal, and `provision()` on approval. This
 * component owns every pixel from that draft appearing to the hand-off
 * into the workspace, and it invents no endpoint of its own: the plan
 * reads are #45's, and Approve/Decline are the SAME
 * `/api/action-proposals/{id}/approve|reject` the inbox uses. Approving a
 * plan is one approval among others, not a special path.
 *
 * WHY THE ROSTER IS RETIRED HERE (P8). `AgentRoster` called
 * `setSpecialistEnabled` directly — a second provisioning path racing the
 * one the plan owns. Enabling a specialist is a CONSEQUENCE of approving
 * the plan, so the terminal asks for the approval and lets provisioning
 * do the enabling. The roster is retired from this path only: with the
 * flag off, `Welcome.tsx` renders it exactly as it shipped.
 *
 * THE FOUR STATES, AND WHY EACH ONE SAYS SOMETHING:
 *
 *   drafting  — a plan is being written. Said in words. A wordless
 *               spinner at the end of a ten-minute interview reads as a
 *               hang, and this is the moment the tenant is deciding
 *               whether the product is real.
 *   draft     — the document, with every unenforced thing LABELLED
 *               unenforced: channels "claimed", envelopes "declared".
 *               A cap the wallet cannot yet enforce is not a cap, and
 *               displaying it as one would be the product's first lie.
 *               `fill_status='refused'` is surfaced, not swallowed.
 *   approved  — what actually got provisioned, and what did NOT, with
 *               the blocker text. A blocked lane is the most useful thing
 *               on the screen; hiding it would mean discovering silence
 *               a week later.
 *   declined  — the plan stays a draft. Nothing was provisioned, and the
 *               screen says so rather than implying a half-state.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Divider,
  InlineStack,
  Text,
} from '@shopify/polaris';

import {
  approveProposal,
  rejectProposal,
} from '../../services/actionProposalsApi';
import {
  getPlan,
  getPlanBets,
  getPlanVersion,
  listPlanVersions,
} from '../../services/planApi';
import type {
  Bet,
  PlanLane,
  PlanResponse,
  PlanVersionDetail,
} from '../../types/plan';
import { isApprovedPlan } from '../../types/plan';

type Phase = 'loading' | 'drafting' | 'draft' | 'approved' | 'declined';

function money(cents: number | null, currency: string | null): string {
  if (cents == null) return 'not declared';
  const amount = (cents / 100).toLocaleString('en-US');
  return `${currency ?? 'USD'} ${amount}`;
}

function LaneBlock({ lane }: { lane: PlanLane }) {
  return (
    <BlockStack gap="100">
      <InlineStack gap="200" blockAlign="center" wrap>
        <Text as="h4" variant="headingXs">
          {lane.display_name}
        </Text>
        <Badge
          tone={
            lane.lane_status === 'blocked'
              ? 'critical'
              : lane.lane_status === 'active'
                ? 'success'
                : undefined
          }
        >
          {lane.lane_status}
        </Badge>
      </InlineStack>
      {lane.mandate ? (
        <Text as="p" variant="bodySm">
          {lane.mandate}
          {lane.cadence ? ` · ${lane.cadence}` : ''}
        </Text>
      ) : null}
      <Text as="p" variant="bodySm" tone="subdued">
        Envelope {money(lane.envelope.declared_cents, lane.envelope.currency)}
        {lane.envelope.enforced ? '' : ' · declared — not yet enforced'}
      </Text>
      {lane.blockers.map((blocker) => (
        <Text key={blocker} as="p" variant="bodySm" tone="critical">
          {blocker}
        </Text>
      ))}
    </BlockStack>
  );
}

function BetBlock({ bet }: { bet: Bet }) {
  return (
    <BlockStack gap="100">
      <Text as="h4" variant="headingXs">
        {bet.label}
      </Text>
      {/* Both faces, always. A bet is a BELIEF someone is acting on and a
          COMMITMENT to act — showing one without the other turns a
          hypothesis into a promise or a promise into a hunch. */}
      <Text as="p" variant="bodySm">
        {bet.belief.statement}
      </Text>
      <Text as="p" variant="bodySm" tone="subdued">
        How we know: {bet.commitment.how_we_know.proxy ?? 'no proxy yet'}
        {bet.commitment.how_we_know.target != null
          ? ` → ${bet.commitment.how_we_know.target}`
          : ''}
        {bet.commitment.locked_at ? '' : ' · not yet committed'}
      </Text>
    </BlockStack>
  );
}

export function PlanTerminalCard({
  onOpenWorkspace,
}: {
  onOpenWorkspace: () => void;
}) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [plan, setPlan] = useState<PlanResponse | null>(null);
  const [draft, setDraft] = useState<PlanVersionDetail | null>(null);
  const [bets, setBets] = useState<Bet[]>([]);
  const [proposalId, setProposalId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [amending, setAmending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const current = await getPlan();
    setPlan(current);

    if (isApprovedPlan(current)) {
      setPhase('approved');
      return;
    }

    setProposalId(current.draft_proposal_id ?? null);
    const versions = await listPlanVersions().catch(() => []);
    const pending =
      versions.find((v) => v.status === 'draft') ?? versions[0] ?? null;
    if (!pending) {
      // No document yet: the drafter is still reading. Say that.
      setPhase('drafting');
      return;
    }
    const [detail, planBets] = await Promise.all([
      getPlanVersion(pending.version),
      getPlanBets().catch(() => [] as Bet[]),
    ]);
    setDraft(detail);
    setBets(planBets);
    setPhase('draft');
  }, []);

  useEffect(() => {
    let cancelled = false;
    load().catch((e) => {
      if (cancelled) return;
      setError(
        e instanceof Error ? e.message : 'Could not read your plan just yet.'
      );
      setPhase('drafting');
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const decide = async (decision: 'approve' | 'decline') => {
    if (!proposalId) return;
    setBusy(true);
    setError(null);
    try {
      if (decision === 'approve') {
        await approveProposal(proposalId);
        // Provisioning happens server-side on approval; re-reading is how
        // we learn what actually came up and what did not.
        await load();
      } else {
        await rejectProposal(proposalId);
        setPhase('declined');
      }
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'That did not go through.'
      );
    } finally {
      setBusy(false);
    }
  };

  if (phase === 'loading' || phase === 'drafting') {
    return (
      <BlockStack gap="200">
        <Text as="h2" variant="headingMd">
          Reading your numbers and drafting a plan…
        </Text>
        <Text as="p">
          I am turning your answers into a goal, the bets I want to take and
          a lane for each agent. Nothing runs until you have read it and
          said yes.
        </Text>
        {error ? <Banner tone="warning">{error}</Banner> : null}
      </BlockStack>
    );
  }

  if (phase === 'declined') {
    return (
      <BlockStack gap="200">
        <Text as="h2" variant="headingMd">
          Nothing was provisioned.
        </Text>
        <Text as="p">
          The plan stays a draft. Change an answer above and I will propose
          a new version — no agent is running in the meantime.
        </Text>
      </BlockStack>
    );
  }

  if (phase === 'approved' && plan && isApprovedPlan(plan)) {
    const blocked = plan.lanes.filter((lane) => lane.lane_status === 'blocked');
    const live = plan.lanes.filter((lane) => lane.lane_status !== 'blocked');
    return (
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          Your plan is in force — v{plan.version}.
        </Text>
        {live.length > 0 && (
          <BlockStack gap="200">
            <Text as="h3" variant="headingSm">
              Provisioned
            </Text>
            {live.map((lane) => (
              <LaneBlock key={lane.key} lane={lane} />
            ))}
          </BlockStack>
        )}
        {blocked.length > 0 && (
          <BlockStack gap="200">
            <Text as="h3" variant="headingSm">
              Blocked, and why
            </Text>
            {/* Named, not hidden: a lane that could not come up is the
                most useful thing on this screen. */}
            {blocked.map((lane) => (
              <LaneBlock key={lane.key} lane={lane} />
            ))}
          </BlockStack>
        )}
        <InlineStack>
          <Button variant="primary" onClick={onOpenWorkspace}>
            Open your workspace
          </Button>
        </InlineStack>
      </BlockStack>
    );
  }

  if (!draft) return null;

  return (
    <BlockStack gap="300">
      <Text as="h2" variant="headingMd">
        Here is the plan I would run.
      </Text>

      {draft.fill_status === 'refused' && (
        <Banner tone="warning">
          <Text as="p">
            Drafted from the template — I would not invent numbers I could
            not read. Connect the missing source and I will redraft against
            your own data.
          </Text>
        </Banner>
      )}

      <BlockStack gap="100">
        <Text as="h3" variant="headingSm">
          The goal
        </Text>
        <Text as="p">
          {draft.goal.success_metric}
          {draft.goal.target != null ? ` → ${draft.goal.target}` : ''}
          {draft.goal.horizon_end
            ? ` by ${new Date(draft.goal.horizon_end).toLocaleDateString('en-US')}`
            : ''}
        </Text>
      </BlockStack>

      {bets.length > 0 && (
        <>
          <Divider />
          <BlockStack gap="200">
            <Text as="h3" variant="headingSm">
              The bets
            </Text>
            {bets.map((bet) => (
              <BetBlock key={bet.id} bet={bet} />
            ))}
          </BlockStack>
        </>
      )}

      <Divider />
      <BlockStack gap="200">
        <Text as="h3" variant="headingSm">
          The lanes
        </Text>
        {draft.lanes.map((lane) => (
          <LaneBlock key={lane.key} lane={lane} />
        ))}
      </BlockStack>

      {draft.owned_channels.length > 0 && (
        <BlockStack gap="100">
          <Text as="h3" variant="headingSm">
            Channels
          </Text>
          {draft.owned_channels.map((channel) => (
            <Text key={channel.key ?? ''} as="p" variant="bodySm">
              {channel.key} · {channel.owner_domain}
              {channel.enforced ? '' : ' — claimed — not yet enforced'}
            </Text>
          ))}
        </BlockStack>
      )}

      {error ? <Banner tone="critical">{error}</Banner> : null}

      <InlineStack gap="200">
        <Button
          variant="primary"
          loading={busy}
          disabled={!proposalId}
          onClick={() => void decide('approve')}
        >
          Approve
        </Button>
        {/* "Amend" is answering the interview again — which is already
            on this page, above. It produces a NEW version rather than
            editing this one in place, which is what keeps the plan an
            append-only record of what was agreed. Navigating away from
            the very questions that would amend it would be the opposite
            of amending, so this reveals the instruction instead. */}
        <Button disabled={busy} onClick={() => setAmending(true)}>
          Amend
        </Button>
        <Button
          tone="critical"
          disabled={busy || !proposalId}
          onClick={() => void decide('decline')}
        >
          Decline
        </Button>
      </InlineStack>
      {amending && (
        <Text as="p" variant="bodySm" tone="subdued">
          Change any answer above and I will draft a new version. This one
          stays a draft in the meantime — nothing runs until you approve a
          version, and settings you edited by hand are left alone.
        </Text>
      )}
      {!proposalId && (
        <Text as="p" variant="bodySm" tone="subdued">
          This draft has no approval waiting yet — refresh in a moment.
        </Text>
      )}
    </BlockStack>
  );
}

export default PlanTerminalCard;
